import { cn } from "@/lib/utils";
import React, { useRef } from "react";
import {
  dashedGridCardPreviewClassName,
  gridCardFooterClassName,
  gridCardPreviewClassName,
  gridCardPreviewFocusedClassName,
  gridCardPreviewSelectedClassName,
  gridCardRootClassName,
  interactiveGridCardClassName,
} from "../constants";

export interface WorkspaceGridCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  preview: React.ReactNode;
  footer: React.ReactNode;
  /** Wraps preview content inside the bordered shell (e.g. dwell preview). */
  thumbnailWrapper?: (node: React.ReactNode) => React.ReactNode;
  isSelected?: boolean;
  dashed?: boolean;
  previewClassName?: string;
  interactive?: boolean;
}

export const WorkspaceGridCard = React.forwardRef<
  HTMLDivElement,
  WorkspaceGridCardProps
>(
  (
    {
      preview,
      footer,
      thumbnailWrapper,
      isSelected = false,
      dashed = false,
      previewClassName,
      interactive = false,
      className,
      onClick,
      onKeyDown,
      onPointerDown,
      role,
      tabIndex,
      ...rootProps
    },
    ref,
  ) => {
    const activatingPointerTypeRef = useRef<PointerEvent["pointerType"] | null>(
      null,
    );

    const previewContent = thumbnailWrapper
      ? thumbnailWrapper(preview)
      : preview;

    const dispatchClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onClick) return;
      const pointerType = activatingPointerTypeRef.current;
      activatingPointerTypeRef.current = null;
      if (pointerType != null) {
        (
          e.nativeEvent as MouseEvent & {
            workspaceActivatingPointerType?: PointerEvent["pointerType"];
          }
        ).workspaceActivatingPointerType = pointerType;
      }
      onClick(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(e);
      if (e.defaultPrevented || !interactive || !onClick) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        dispatchClick(e as unknown as React.MouseEvent<HTMLDivElement>);
      }
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      activatingPointerTypeRef.current = e.pointerType;
      onPointerDown?.(e);
    };

    const interactiveProps = interactive
      ? {
          role: role ?? "button",
          tabIndex: tabIndex ?? 0,
          onKeyDown: handleKeyDown,
        }
      : {};

    return (
      <div
        ref={ref}
        className={cn(
          gridCardRootClassName,
          interactive && interactiveGridCardClassName,
          className,
        )}
        onPointerDown={handlePointerDown}
        onClick={onClick ? dispatchClick : undefined}
        {...interactiveProps}
        {...rootProps}
      >
        <div
          className={cn(
            gridCardPreviewClassName,
            gridCardPreviewFocusedClassName,
            dashed && dashedGridCardPreviewClassName,
            isSelected && gridCardPreviewSelectedClassName,
            previewClassName,
          )}
        >
          {previewContent}
        </div>
        <div className={gridCardFooterClassName}>{footer}</div>
      </div>
    );
  },
);

WorkspaceGridCard.displayName = "WorkspaceGridCard";
